import type { User } from '@repo-level/core-front';
import { AuthContext } from '@repo-level/core-front';
import { AddonsContext } from 'contexts/addons';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AssignABTestVersionParams } from '../helpers/assignABTestVersion';
import { assignABTestVersion as assignABTestVersionFunction } from '../helpers/assignABTestVersion';
import { checkAbTestVersion } from '../helpers/checkAbTestVersion';
import type { ABTestVersion } from '../types';

interface UseABTestingV2Params {
  experiment: string;
  versions?: ABTestVersion[];
}

const WAIT_TIMEOUT_MS = 10_000;

export const useABTestingV2 = ({
  experiment,
  versions,
}: UseABTestingV2Params) => {
  const [version, setVersion] = useState<string | undefined>();

  const { cmsABTests, pendingCmsAbTests } = useContext(AddonsContext);
  const { user, refreshUser, pendingUser } = useContext(AuthContext);

  const isMounted = useRef(true);
  const waitResolver = useRef<(() => void) | null>(null);

  const normalizedVersions = useMemo(
    () => versions ?? createDefaultVersions(experiment),
    [versions, experiment]
  );

  const isPending = pendingCmsAbTests || pendingUser;

  const waitUntilReady = useCallback(() => {
    if (!isPending) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const timeoutId = setTimeout(() => {
        // Prevent infinite await if pending is stuck
        waitResolver.current = null;
        resolve();
      }, WAIT_TIMEOUT_MS);

      waitResolver.current = () => {
        clearTimeout(timeoutId);
        resolve();
      };
    });
  }, [isPending]);

  useEffect(() => {
    if (isPending) return;

    if (waitResolver.current) {
      waitResolver.current();
      waitResolver.current = null;
    }
  }, [isPending]);

  const executeCheck = useCallback(() => {
    const checkVersion = checkAbTestVersion({
      user,
      experiment,
      cmsABTests,
      refreshUser,
      versions: normalizedVersions,
    });

    if (isMounted.current) {
      setVersion(checkVersion);
    }

    return formatVersion(normalizedVersions, checkVersion);
  }, [user, experiment, cmsABTests, refreshUser, normalizedVersions]);

  const executeAssign = useCallback(
    (params: Omit<AssignABTestVersionParams, 'versions'>) => {
      const assignVersion = assignABTestVersionFunction({
        ...params,
        versions: normalizedVersions,
      });

      if (isMounted.current) {
        setVersion(assignVersion);
      }

      return formatVersion(normalizedVersions, assignVersion);
    },
    [normalizedVersions]
  );

  const assignABTestVersion = useCallback(
    async (targetUser?: User) => {
      await waitUntilReady();
      return executeAssign({
        user: targetUser ?? user,
        experiment,
        cmsABTests,
        refreshUser,
      });
    },
    [cmsABTests, executeAssign, experiment, refreshUser, user, waitUntilReady]
  );

  useEffect(() => {
    if (isPending) return;
    if (version) return;

    executeCheck();
  }, [isPending, version, executeCheck]);

  useEffect(
    () => () => {
      isMounted.current = false;
      waitResolver.current = null;
    },
    []
  );

  return useMemo(
    () => ({
      isLoading: isPending && !version,
      ...formatVersion(normalizedVersions, version),
      assignABTestVersion,
    }),
    [isPending, version, normalizedVersions, assignABTestVersion]
  );
};

const formatVersion = (versions: ABTestVersion[], version?: string) => ({
  version,
  isA: version === versions[0].name,
  isB: version === versions[1].name,
});

const createDefaultVersions = (experiment: string): ABTestVersion[] => [
  { name: `${experiment}:a`, probability: 50 },
  { name: `${experiment}:b`, probability: 50 },
];
